import React from 'react';
import { ClipboardText, ListChecks, Warning } from '@phosphor-icons/react';
import { EXPLORATORY_EFFORT_MINUTES, type ExploratoryEffort } from '@chatsdet/types';
import { Flex, Progress, TagBadge, Text, type TagColor } from '@chatsdet/ui';
import type {
  ExploratoryTestLiveChecklistItem,
  ExploratoryTestListItem,
  FuzzReportJson,
} from '../types';

type AttackSurfaceStatus = FuzzReportJson['attackSurface'][number]['status'];
type AttackSurfaceRow = {
  id: string;
  area: string;
  status: AttackSurfaceStatus;
  result: string | null;
};

const STATUS_BADGE: Record<AttackSurfaceStatus, { label: string; color: TagColor }> = {
  clean: { label: 'Clean', color: 'green' },
  bug_found: { label: 'Bug found', color: 'red' },
  blocked: { label: 'Blocked', color: 'yellow' },
  untried: { label: 'Not attacked', color: 'gray' },
};

const STOP_REASON_TEXT: Record<FuzzReportJson['stopReason'], string> = {
  saturated: 'Stopped early: several attacks in a row found nothing new',
  budget_used: 'Used its full time',
  agent_finished: 'Finished on its own',
  no_report: 'Ended without a final report',
};

/** The fuzzer persists its list as pending / passed / failed / skipped (see `SURFACE_TO_LIVE_STATUS` in the API's fuzz-run.ts). */
function surfaceStatusOf(status: ExploratoryTestLiveChecklistItem['status']): AttackSurfaceStatus {
  switch (status) {
    case 'passed':
      return 'clean';
    case 'failed':
      return 'bug_found';
    case 'skipped':
      return 'blocked';
    default:
      return 'untried';
  }
}

const IN_FLIGHT_STATUSES: ReadonlyArray<ExploratoryTestListItem['status']> = [
  'pending',
  'planning',
  'running',
];

function isFuzzReport(report: unknown): report is FuzzReportJson {
  return typeof report === 'object' && report !== null && (report as { kind?: unknown }).kind === 'fuzz';
}

function getFuzzReport(test: ExploratoryTestListItem): FuzzReportJson | null {
  return isFuzzReport(test.reportJson) ? test.reportJson : null;
}

function SectionTitle({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <Flex align="center" gap="sm" className="mb-1.5 px-3">
      {icon}
      <Text weight="medium" size="xs" className="uppercase tracking-wide text-muted-foreground">
        {children}
      </Text>
    </Flex>
  );
}

/** Minutes elapsed since `startedAt`, re-rendered on a light interval while `isActive`. */
function useElapsedMinutes(startedAt: string | null, isActive: boolean): number {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!isActive) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 5_000);
    return () => window.clearInterval(timer);
  }, [isActive]);
  if (!startedAt) return 0;
  return Math.max(0, (now - new Date(startedAt).getTime()) / 60_000);
}

/**
 * Time against the effort's budget. While running it is time since the run started
 * (environment setup included, so it can read ahead of the run's own clock); once finished
 * it is the report's minutes, or the run's duration when it ended without a report.
 */
function FuzzBudgetBar({ test }: { test: ExploratoryTestListItem }) {
  const report = getFuzzReport(test);
  const isInFlight = IN_FLIGHT_STATUSES.includes(test.status);
  const elapsed = useElapsedMinutes(test.startedAt, isInFlight);
  const effort: ExploratoryEffort | null = test.effort ?? null;
  if (!effort) return null;

  const budget = EXPLORATORY_EFFORT_MINUTES[effort];
  let used: number;
  if (isInFlight) used = elapsed;
  else if (report) used = report.minutesUsed;
  else if (test.startedAt && test.finishedAt)
    used = (new Date(test.finishedAt).getTime() - new Date(test.startedAt).getTime()) / 60_000;
  else return null;
  const usedLabel = used < 10 ? used.toFixed(1) : String(Math.round(used));

  return (
    <div className="px-3">
      <Flex align="center" justify="between" className="mb-1">
        <Text size="xs" weight="medium" className="text-muted-foreground">
          {isInFlight ? 'Elapsed' : 'Time used'}
        </Text>
        <Text size="xs" className="tabular-nums text-muted-foreground">
          {usedLabel} of {budget} min
        </Text>
      </Flex>
      <Progress
        value={Math.min(1, used / budget)}
        size="md"
        animate={false}
        aria-label={`Time used: ${usedLabel} of ${budget} minutes`}
      />
    </div>
  );
}

function FuzzStopReason({ reason }: { reason: FuzzReportJson['stopReason'] }) {
  return (
    <Text size="sm" className="text-foreground">
      {STOP_REASON_TEXT[reason]}
    </Text>
  );
}

function AttackSurfaceTable({ rows }: { rows: ReadonlyArray<AttackSurfaceRow> }) {
  if (rows.length === 0) {
    return (
      <div className="px-3">
        <Text size="sm" className="text-muted-foreground">
          The fuzzer has not mapped any areas yet.
        </Text>
      </div>
    );
  }
  return (
    <div className="divide-y divide-border/60 border-y border-border/60">
      {rows.map((row) => {
        const badge = STATUS_BADGE[row.status];
        return (
          <div key={row.id} className="px-3 py-2">
            <Flex align="start" justify="between" gap="sm">
              <Text size="xs" weight="medium" className="min-w-0 flex-1 text-foreground">
                {row.area}
              </Text>
              <TagBadge name={badge.label} color={badge.color} size="sm" hideDot />
            </Flex>
            {row.result && (
              <Text size="xs" className="mt-0.5 leading-relaxed text-muted-foreground">
                {row.result}
              </Text>
            )}
          </div>
        );
      })}
    </div>
  );
}

function liveRows(items: ReadonlyArray<ExploratoryTestLiveChecklistItem>): AttackSurfaceRow[] {
  return items.map((item) => ({
    id: item.id,
    area: item.name,
    status: surfaceStatusOf(item.status),
    result: item.details ?? null,
  }));
}

function attackSurfaceRows(test: ExploratoryTestListItem): AttackSurfaceRow[] {
  const report = getFuzzReport(test);
  return report ? report.attackSurface : liveRows(test.liveTestPlan ?? []);
}

/** Plan tab for a fuzz run: focus, budget and the attack surface, live while running. */
export function FuzzPlanPanel({ test }: { test: ExploratoryTestListItem }) {
  const isInFlight = IN_FLIGHT_STATUSES.includes(test.status);
  const rows = attackSurfaceRows(test);
  return (
    <div className="h-full overflow-y-auto space-y-4 py-3">
      {test.instructions && (
        <div className="px-3">
          <SectionTitle icon={<ClipboardText size={14} weight="duotone" />}>Focus</SectionTitle>
          <Text size="sm" className="whitespace-pre-wrap leading-relaxed text-foreground">
            {test.instructions}
          </Text>
        </div>
      )}
      <FuzzBudgetBar test={test} />
      <div>
        <SectionTitle icon={<ListChecks size={14} weight="duotone" />}>
          Attack surface ({rows.length})
        </SectionTitle>
        {isInFlight && rows.length === 0 ? (
          <div className="px-3">
            <Text size="sm" className="text-muted-foreground">
              The plan grows as the fuzzer finds new areas to attack.
            </Text>
          </div>
        ) : (
          <AttackSurfaceTable rows={rows} />
        )}
      </div>
    </div>
  );
}

function CountChips({ entries }: { entries: ReadonlyArray<[string, number]> }) {
  return (
    <Flex gap="xs" className="flex-wrap">
      {entries.map(([name, count]) => (
        <span
          key={name}
          className="rounded bg-muted px-1.5 py-0.5 text-xs tabular-nums text-foreground"
        >
          {name} {count}
        </span>
      ))}
    </Flex>
  );
}

/** Report tab for a fuzz run. */
export function FuzzReportPanel({ test }: { test: ExploratoryTestListItem }) {
  const report = getFuzzReport(test);
  const isInFlight = IN_FLIGHT_STATUSES.includes(test.status);

  if (!report) {
    if (isInFlight) {
      return (
        <div className="h-full overflow-y-auto space-y-4 py-3">
          <FuzzBudgetBar test={test} />
          <AttackSurfaceTable rows={liveRows(test.liveTestPlan ?? [])} />
        </div>
      );
    }
    return (
      <div className="flex h-full items-center justify-center px-4 text-center text-muted-foreground">
        <Text size="sm">No report yet. The fuzzer writes a final report when it finishes.</Text>
      </div>
    );
  }

  const { findings } = report;
  const severityEntries = (
    [
      ['High', findings.bySeverity.high],
      ['Medium', findings.bySeverity.medium],
      ['Low', findings.bySeverity.low],
    ] as Array<[string, number]>
  ).filter(([, count]) => count > 0);
  const categoryEntries = Object.entries(findings.byCategory).filter(([, count]) => count > 0);

  return (
    <div className="h-full overflow-y-auto space-y-4 py-3">
      <div className="px-3">
        <SectionTitle icon={<ClipboardText size={14} weight="duotone" />}>Summary</SectionTitle>
        <Text size="sm" className="whitespace-pre-wrap leading-relaxed text-foreground">
          {report.summary}
        </Text>
      </div>

      <div className="space-y-2 px-3">
        <FuzzStopReason reason={report.stopReason} />
      </div>
      <FuzzBudgetBar test={test} />

      <div className="space-y-1.5 px-3">
        <Text size="xs" weight="medium" className="uppercase tracking-wide text-muted-foreground">
          Bugs found ({findings.total})
        </Text>
        {findings.total === 0 ? (
          <Text size="sm" className="text-foreground">
            No bugs found. The attack surface below shows what was tried.
          </Text>
        ) : (
          <>
            <CountChips entries={severityEntries} />
            <CountChips entries={categoryEntries} />
          </>
        )}
      </div>

      <div className="px-3">
        <Text size="xs" className="tabular-nums text-muted-foreground">
          {report.environmentsUsed} {report.environmentsUsed === 1 ? 'environment' : 'environments'}{' '}
          and {report.sessionsUsed} browser {report.sessionsUsed === 1 ? 'session' : 'sessions'} used
        </Text>
      </div>

      {report.coverageGaps.length > 0 && (
        <div>
          <SectionTitle icon={<Warning size={14} weight="duotone" />}>
            Coverage gaps ({report.coverageGaps.length})
          </SectionTitle>
          <ul className="list-disc space-y-1 pl-8 pr-3">
            {report.coverageGaps.map((gap, i) => (
              <li key={i}>
                <Text size="xs" className="leading-relaxed text-foreground">
                  {gap}
                </Text>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <SectionTitle icon={<ListChecks size={14} weight="duotone" />}>
          Attack surface ({report.attackSurface.length})
        </SectionTitle>
        <AttackSurfaceTable rows={report.attackSurface} />
      </div>
    </div>
  );
}
